interface ITimestampProps {
  id?: number;
  text: string;
  timestamp: number;
  mainCode: string;
  c1Code: string;
  c2Code: string;
}

export class Timestamp {
  id?: number;
  text: string;
  timestamp: number;
  mainCode: string;
  c1Code: string;
  c2Code: string;

  constructor(data: ITimestampProps) {
    this.id = data.id;
    this.text = data.text;
    this.timestamp = data.timestamp;
    this.mainCode = data.mainCode;
    this.c1Code = data.c1Code;
    this.c2Code = data.c2Code;
  }

  isBefore(time: number): boolean {
    return this.timestamp < time;
  }

  isBeforeOrEqual(time: number): boolean {
    return this.timestamp <= time;
  }

  isAfter(time: number): boolean {
    return this.timestamp > time;
  }

  isAfterOrEqual(time: number): boolean {
    return this.timestamp >= time;
  }

  isAt(time: number): boolean {
    return this.timestamp === time;
  }

  isEqualWith(timestamp: Timestamp): boolean {
    return this.id === timestamp.id;
  }

  isSameTimeWith(timestamp: Timestamp): boolean {
    return this.timestamp === timestamp.timestamp;
  }
}

export const emptyTimestamp: ITimestampProps = {
  id: 0,
  text: "",
  timestamp: 0,
  mainCode: "",
  c1Code: "",
  c2Code: ""
};

export class DoNotJumpTimestamp extends Timestamp {
  constructor() {
    super(emptyTimestamp);
  }
}

export class Timestamps {
  items: Timestamp[];
  urlType?: string;

  constructor(data: Timestamp[], urlType: string | undefined) {
    this.items = data;
    this.urlType = urlType;
  }

  getById(id: number) {
    return this.items.filter((x) => x.id === id)?.shift();
  }

  outOfRange(currentTimestamp: Timestamp): boolean {
    const index = this.items.indexOf(currentTimestamp);
    return index < 0 || index >= this.items.length;
  }

  getTimestampUrl(currentTimestamp: Timestamp): string {
    return this.urlType ? this.urlType.replace(":id", String(currentTimestamp.id)) : "";
  }

  filterOutOnesOutOfVideoRange(videoDuration: number): Timestamps {
    return new Timestamps(
      this.items.filter((x) => x.isBefore(videoDuration)),
      this.urlType
    );
  }

  isTimeAfterLastTimestamp(time: number): boolean {
    return this.items[this.items.length - 1]?.isBeforeOrEqual(time);
  }

  isTimeBeforeFirstTimestamp(time: number): boolean {
    return this.items[0]?.isAfterOrEqual(time);
  }

  isFirstTimeStamp(currentTimestamp: Timestamp): boolean {
    return this.items[0]?.isEqualWith(currentTimestamp);
  }

  isLastTimeStamp(currentTimestamp: Timestamp): boolean {
    return this.items[this.items.length - 1]?.isEqualWith(currentTimestamp);
  }

  isEmpty(): boolean {
    return this.items.length === 0;
  }

  getPreviousTimestamp(currentTimestamp: Timestamp): Timestamp | undefined {
    const index = this.items.indexOf(currentTimestamp);
    return index > 0 && index <= this.items.length - 1 ? this.items[index - 1] : undefined;
  }

  getNextTimestamp(currentTimestamp: Timestamp): Timestamp | undefined {
    const index = this.items.indexOf(currentTimestamp);
    return index >= 0 && index < this.items.length - 1 ? this.items[index + 1] : undefined;
  }

  getTimestampAt(time: number): Timestamp | undefined {
    return this.items.find((x) => x.isAt(time));
  }

  getLastTimestampCloseTo(time: number): Timestamp | undefined {
    for (let index = this.items.length - 1; index >= 0; index--) {
      if (this.items[index].isBefore(time))
        return this.items[index];
    }
  }

  getFirstTimestampCloseTo(time: number): Timestamp | undefined {
    return this.items.filter((t) => t.isAfter(time))?.shift();
  }
}
